import React, { useState } from 'react';
import { X, MapPin } from 'lucide-react';
import SitePinPicker from './sitePinPicker.jsx';
import { PROVINCES, provinceCenter } from './provinces.jsx';
import { numberToPersianWords, digitsOnly, formatMoney, faDigits, currentJalaliYear } from './persianMoney.jsx';

const MIN_BUILD_YEAR = 1370;

// سنِ بنا: دو ورودیِ کنارِ هم — «چند سال» (عدد) و منوی بازشونده‌ی «سالِ ساخت» (امسال تا ۱۳۷۰). هر کدام پر شود،
// دیگری خودکار حساب می‌شود. age (سال) در buildYear و سالِ ساخت در constructionYear ذخیره می‌شود
function BuildAgeField({ age, year, onChange }) {
  const thisYear = currentJalaliYear();
  const years = [];
  for (let y = thisYear; y >= MIN_BUILD_YEAR; y -= 1) years.push(y);
  const yearNum = Number(year);
  const selectValue = !year ? '' : yearNum < MIN_BUILD_YEAR ? 'older' : String(yearNum);
  return (
    <div className="create-plan-form__field">
      <span className="create-plan-form__label">سنِ بنا</span>
      <div className="create-plan-form__age">
        <div className="create-plan-form__age-input">
          <input
            type="text"
            inputMode="numeric"
            placeholder="مثلاً ۱۵"
            value={faDigits(age)}
            onChange={(e) => {
              const a = digitsOnly(e.target.value).slice(0, 3);
              onChange(a, a === '' ? '' : String(thisYear - Number(a)));
            }}
          />
          <span>سال</span>
        </div>
        <select
          className="create-plan-form__select create-plan-form__age-year"
          value={selectValue}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return onChange('', '');
            if (v === 'older') return onChange(String(thisYear - (MIN_BUILD_YEAR - 1)), String(MIN_BUILD_YEAR - 1));
            const y = Number(v);
            return onChange(String(thisYear - y), String(y));
          }}
        >
          <option value="">سالِ ساخت…</option>
          {years.map((y) => (
            <option key={y} value={String(y)}>
              {`ساخت ${faDigits(y)}${y === thisYear ? ' (نوساز)' : ''}`}
            </option>
          ))}
          <option value="older">{`قبل از ${faDigits(MIN_BUILD_YEAR)}`}</option>
        </select>
      </div>
    </div>
  );
}

// مبلغ: سه‌رقم‌سه‌رقم جدا (ارقامِ فارسی) و زیرش به حروف — «چهار میلیارد و دویست میلیون تومان»
function MoneyField({ label, value, onChange, placeholder }) {
  const words = numberToPersianWords(value);
  return (
    <label className="create-plan-form__field">
      <span className="create-plan-form__label">{label}</span>
      <input
        type="text"
        inputMode="numeric"
        dir="ltr"
        className="create-plan-form__money"
        placeholder={placeholder}
        value={formatMoney(value)}
        onChange={(e) => onChange(digitsOnly(e.target.value).slice(0, 15))}
      />
      {words && <small className="create-plan-form__money-words">{words} تومان</small>}
    </label>
  );
}
import './createPlanForm.scss';

const DEAL_TYPES = [
  { id: 'sale', label: 'فروش' },
  { id: 'rent', label: 'رهن و اجاره' },
];

const PROPERTY_TYPES = [
  { id: 'apartment', label: 'آپارتمان' },
  { id: 'villa', label: 'ویلایی' },
  { id: 'land', label: 'زمین' },
  { id: 'office', label: 'اداری' },
  { id: 'shop', label: 'مغازه' },
];

const AMENITIES = [
  { id: 'elevator', label: 'آسانسور' },
  { id: 'parking', label: 'پارکینگ' },
  { id: 'storage', label: 'انباری' },
  { id: 'balcony', label: 'بالکن' },
];

// جهتِ ملک (قراردادِ بازار): «شمالی» = ملک در ضلعِ شمالیِ خیابان است، پس خیابان سمتِ جنوبِ ساختمان است؛
// «جنوبی» برعکس؛ «شرقی/غربی» هم به همین ترتیب. در سه‌بعدی تعیین می‌کند خیابان پشتِ کدام دیوار است
const ORIENTATIONS = [
  { id: 'north', label: 'شمالی' },
  { id: 'south', label: 'جنوبی' },
  { id: 'east', label: 'شرقی' },
  { id: 'west', label: 'غربی' },
];

const initialForm = {
  dealType: 'sale',
  propertyType: 'apartment',
  title: '',
  description: '',
  price: '',
  deposit: '',
  rent: '',
  area: '',
  rooms: '',
  floor: '',
  totalFloors: '',
  orientation: '',
  province: '', // استان — اول انتخاب می‌شود، بعد نقشه‌ی لوکیشن روی همان استان باز می‌شود (فیلترِ اکسپلور)
  site: null, // { lat, lng } — لوکیشنِ دقیقِ ساختمان
  buildYear: '', // سنِ بنا (سال)
  constructionYear: '', // سالِ ساخت (شمسی)
  amenities: [],
  photos: [],
};

function toggleArrayItem(arr, item) {
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
}

function CreatePlanForm({ onClose, onSubmit }) {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState('');
  const [pinOpen, setPinOpen] = useState(false);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    // اجباری: عنوان، متراژ، استان و لوکیشنِ روی نقشه (برای جست‌وجوی محدوده و «هشدارِ آگهیِ جدید»)؛
    // جهتِ ملک، طبقه و تعدادِ طبقات اختیاری‌اند
    if (!form.title.trim() || !form.area) {
      setError('لطفاً عنوان و متراژ را وارد کنید');
      return;
    }
    if (!form.province) {
      setError('لطفاً استانِ ملک را انتخاب کنید');
      return;
    }
    if (!form.site) {
      setError('لطفاً لوکیشنِ ساختمان را روی نقشه مشخص کنید');
      return;
    }
    if (form.floor !== '' && form.totalFloors !== '' && Number(form.floor) > Number(form.totalFloors)) {
      setError('طبقه نمی‌تواند از تعدادِ کلِ طبقات بیشتر باشد');
      return;
    }
    setError('');
    onSubmit?.(form);
  };

  return (
    <div className="create-plan-form">
      <div className="create-plan-form__backdrop" onClick={onClose} />
      <div className="create-plan-form__sheet">
        <div className="create-plan-form__header">
          <h2>ثبت فایل جدید</h2>
          <button type="button" className="create-plan-form__close" onClick={onClose} aria-label="بستن">
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        <form className="create-plan-form__body" onSubmit={handleSubmit}>
          <div className="create-plan-form__field">
            <span className="create-plan-form__label">نوع معامله</span>
            <div className="create-plan-form__segment">
              {DEAL_TYPES.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={form.dealType === d.id ? 'is-active' : ''}
                  onClick={() => update('dealType', d.id)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div className="create-plan-form__field">
            <span className="create-plan-form__label">نوع ملک</span>
            <div className="create-plan-form__chips">
              {PROPERTY_TYPES.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`create-plan-form__chip ${form.propertyType === p.id ? 'is-selected' : ''}`}
                  onClick={() => update('propertyType', p.id)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <label className="create-plan-form__field">
            <span className="create-plan-form__label">عنوان آگهی</span>
            <input
              type="text"
              placeholder="مثلاً: فروش آپارتمان ۸۵ متری دو خواب"
              value={form.title}
              onChange={(e) => update('title', e.target.value)}
            />
          </label>

          <label className="create-plan-form__field">
            <span className="create-plan-form__label">توضیحات</span>
            <textarea
              rows={4}
              placeholder="توضیحات کامل ملک را بنویسید..."
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
            />
          </label>

          {form.dealType === 'sale' && (
            <MoneyField label="قیمت (تومان)" placeholder="مثلاً ۴٬۲۰۰٬۰۰۰٬۰۰۰" value={form.price} onChange={(v) => update('price', v)} />
          )}

          {form.dealType === 'rent' && (
            <div className="create-plan-form__row">
              <MoneyField label="ودیعه (تومان)" value={form.deposit} onChange={(v) => update('deposit', v)} />
              <MoneyField label="اجاره ماهیانه (تومان)" value={form.rent} onChange={(v) => update('rent', v)} />
            </div>
          )}

          <div className="create-plan-form__row">
            <label className="create-plan-form__field">
              <span className="create-plan-form__label">متراژ (متر)</span>
              <input
                type="text"
                inputMode="numeric"
                value={faDigits(form.area)}
                onChange={(e) => update('area', digitsOnly(e.target.value))}
              />
            </label>
            <label className="create-plan-form__field">
              <span className="create-plan-form__label">تعداد اتاق</span>
              <input
                type="text"
                inputMode="numeric"
                value={faDigits(form.rooms)}
                onChange={(e) => update('rooms', digitsOnly(e.target.value))}
              />
            </label>
          </div>

          <div className="create-plan-form__row">
            <label className="create-plan-form__field">
              <span className="create-plan-form__label">طبقه (همکف = ۰)</span>
              <input
                type="text"
                inputMode="numeric"
                min={0}
                value={faDigits(form.floor)}
                onChange={(e) => update('floor', digitsOnly(e.target.value))}
              />
            </label>
            <label className="create-plan-form__field">
              <span className="create-plan-form__label">تعدادِ کلِ طبقات (اختیاری)</span>
              <input
                type="text"
                inputMode="numeric"
                min={1}
                value={faDigits(form.totalFloors)}
                onChange={(e) => update('totalFloors', digitsOnly(e.target.value))}
              />
            </label>
          </div>

          <BuildAgeField
            age={form.buildYear}
            year={form.constructionYear}
            onChange={(a, y) => setForm((prev) => ({ ...prev, buildYear: a, constructionYear: y }))}
          />

          <div className="create-plan-form__field">
            <span className="create-plan-form__label">جهتِ ملک (اختیاری)</span>
            <div className="create-plan-form__segment">
              {ORIENTATIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  className={form.orientation === o.id ? 'is-active' : ''}
                  onClick={() => update('orientation', o.id)}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>


          <label className="create-plan-form__field">
            <span className="create-plan-form__label">استان</span>
            <select
              className="create-plan-form__select"
              value={form.province}
              onChange={(e) => {
                // با عوضِ استان، لوکیشنِ قبلی (که مالِ استانِ دیگری بود) پاک می‌شود
                setForm((prev) => ({ ...prev, province: e.target.value, site: prev.province === e.target.value ? prev.site : null }));
              }}
            >
              <option value="">انتخابِ استان…</option>
              {PROVINCES.map((pr) => (
                <option key={pr.name} value={pr.name}>
                  {pr.name}
                </option>
              ))}
            </select>
          </label>

          <div className="create-plan-form__field">
            <span className="create-plan-form__label">لوکیشنِ ساختمان</span>
            <button
              type="button"
              className={`create-plan-form__site ${form.site ? 'is-set' : ''}`}
              onClick={() => form.province && setPinOpen(true)}
              disabled={!form.province}
            >
              <MapPin size={18} strokeWidth={1.8} />
              <span>
                {!form.province
                  ? 'اول استان را انتخاب کنید'
                  : form.site
                    ? `ثبت شد (${form.site.lat.toFixed(5)}، ${form.site.lng.toFixed(5)}) — برای تغییر بزنید`
                    : `مشخص کردن روی نقشه‌ی ${form.province}`}
              </span>
            </button>
          </div>

          <div className="create-plan-form__field">
            <span className="create-plan-form__label">امکانات</span>
            <div className="create-plan-form__chips">
              {AMENITIES.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={`create-plan-form__chip ${form.amenities.includes(a.id) ? 'is-selected' : ''}`}
                  onClick={() => update('amenities', toggleArrayItem(form.amenities, a.id))}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="create-plan-form__error">{error}</p>}

          <button type="submit" className="create-plan-form__submit">
            ثبت اطلاعات
          </button>
        </form>
      </div>
      {pinOpen && (
        <SitePinPicker
          initial={form.site}
          center={provinceCenter(form.province)}
          onClose={() => setPinOpen(false)}
          onConfirm={(site) => update('site', site)}
        />
      )}
    </div>
  );
}

export default CreatePlanForm;